import {useEffect, useMemo, useState} from 'react'
import useTenantList from '../../api/getTenantList'
import CustomSearchableDropdown from '../Common/CustomSearchableDropdown'
import {useDispatch, useSelector} from 'react-redux'
import {
  setAzureRole,
  setTenantDataError,
  setTenantDataLoading,
  setTenantList
} from '../../store/superuser/superUserSlice'
import {
  setButtonViewName,
  setCurrentSelectedFilter,
  setFilters,
  setSearchQuery,
  setSearchTermQuery,
  setSwitchFilters,
  setUniqueConstituentIdentifier
} from '../../store/askGivePrism/askGivePrismSlice'
import {
  getAllProspectsData,
  getAllSavedProspectsData,
  setSortColumn,
  setSortConfig,
  setSortDirection
} from '../../store/prospectSegment/allProspectsData/allProspectsDataSlice'
import {
  getProspectsPageNumber,
  getSavedProspectsPageNumber,
  setAllConstituentsSavedProspectsListPageNumber
} from '../../store/pagination/pageNumberSlice'
import CustomDialog from '../Common/CustomDialog'
import {StyledActionBox} from '../Common/Styles'
import {StyledActionAddButton, StyledActionResetButton} from '../Campaigns/AskGivePrism/Styles'
import {
  setChatbotData,
  setInterestTagId,
  setMatchedProspectId,
  setMatchedProspectList,
  setMessage,
  setSessionId,
  setShouldResetChatView
} from '../../store/askGivePrism/chatbot/chatbotStreamSlice'
import {selectedTab} from '../../store/prospectSegment/selectedProspectsTab/selectedProspectsTabSlice'
import theme from '../../themes'
import {StyledTitleContainer} from '../Common/ProfileCircle/ProfileCircleStyles'
import {Typography} from '@mui/material'
import PropTypes from 'prop-types'
import {setRefData} from '../../store/opportunityBank/opportunityBankSlice'
import {setCampaignView} from '../../store/selectedView/campaignView/campaignViewIndex'
import {
  setAppliedInterestTags,
  setCategorisedInterestTagRefData,
  setCategoryTagName,
  setRemoveSpecificCategoryTagRefData,
  setSearchedInterestTag,
  setSelectedInterestOptions,
  setSpecificCategoryTagRefData,
  setTotalInterestTagCount
} from '../../store/askGivePrism/categorizedInterestTagSlice'
import {setCampaignAction} from '../../store/selectedView/campaignView/campaignActionSlice'
import {setAddToSavedProspects} from '../../store/prospectSegment/addToSavedProspects/addToSavedProspectsSlice'
import {count, savedProspectsCount} from '../../store/prospectSegment/allProspectsCount/allProspectsCountSlice'
import {setAllConstituentsTotalCount} from '../../store/prospectSegment/openSavedProspect/openSavedProspectSlice'
import {
  allSavedProspectsList,
  constituentIdList,
  setListDescription
} from '../../store/prospectSegment/savedProspectsList/saveProspectsListSlice'
import {
  setResetSearchTerm,
  updatedAllProspectsSearchTerm,
  updatedSavedProspectsSearchTerm
} from '../../store/search/searchSlice'
import useSessionManager from '../../hooks/useSessionManager'

const TenantConfirmationPopup = ({selectedTenantName}) => {
  return (
    <>
      <Typography
        sx={{color: 'black', fontSize: '12px'}}
      >{`Are you sure you want to switch to the tenant "${selectedTenantName}"?`}</Typography>
      <Typography sx={{color: 'red', fontSize: '10px', fontStyle: 'italic'}}>
        This action will reset your current dashboard view and all the data will be lost.
      </Typography>
    </>
  )
}

const SuperUserIndex = () => {
  const [getTenantListData, {data, loading, error}] = useTenantList()
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const dispatch = useDispatch()

  const azureRole = useSelector((state) => state.superUserTenantSlice.azureRole)
  const sessionId = useSelector((state) => state.chatBotSlice.sessionId)
  const {deleteSession} = useSessionManager()

  useEffect(() => {
    getTenantListData()
  }, [getTenantListData])

  // Transform tenant list data using useMemo
  const transformedTenantList = useMemo(() => {
    return (
      data?.tenantList?.tenantListType?.map((tenant, index) => ({
        name: tenant.tenantName,
        role: tenant.azureRole,
        id: index
      })) || []
    )
  }, [data])

  useEffect(() => {
    dispatch(setTenantDataLoading(loading))
    dispatch(setTenantDataError(error))
  }, [loading, error, dispatch])

  useEffect(() => {
    if (transformedTenantList.length > 0) {
      setSelectedTenant(transformedTenantList[0])
      dispatch(setAzureRole(transformedTenantList[0].role))
    }
  }, [transformedTenantList, dispatch])

  const handleCancel = () => {
    const previousRole = transformedTenantList.find((tenant) => tenant.role === azureRole)
    setSelectedTenant(previousRole)
    setOpenDialog(false)
  }

  const handleValueChange = (value) => {
    setSelectedTenant(value)
    setOpenDialog(true)
  }

  const handleConfirm = async () => {
    dispatch(setTenantList(transformedTenantList))
    dispatch(setAzureRole(selectedTenant.role))

    // reset the initial data
    dispatch(setFilters({})) // reset filters for ask give prism
    dispatch(getAllProspectsData([])) // reset all prospects data
    dispatch(getAllSavedProspectsData([])) // reset all saved prospects data
    dispatch(setRefData(null)) // reset opportunity bank ref data
    dispatch(setCategorisedInterestTagRefData(null)) // reset categorized interest tag ref data

    // reset the chatbot
    if (sessionId) {
      await deleteSession(sessionId)
      dispatch(setSessionId(null))
    }

    // reset pagination page numbers
    dispatch(getProspectsPageNumber(1)) // reset all prospects page number
    dispatch(getSavedProspectsPageNumber(1)) // reset all saved prospects page number
    dispatch(setAllConstituentsSavedProspectsListPageNumber(1)) // reset all constituents saved prospects list page number
    dispatch(setUniqueConstituentIdentifier([])) // reset unique constituent identifier

    // reset category based interest tags
    dispatch(setCategoryTagName('')) // reset category tag name
    dispatch(setSpecificCategoryTagRefData([])) // reset specific category tag ref data
    dispatch(setTotalInterestTagCount([])) // reset searched interest tag
    dispatch(setRemoveSpecificCategoryTagRefData([])) // reset specific category tag ref data
    dispatch(setSearchedInterestTag('')) // reset searched interest tag
    dispatch(setSelectedInterestOptions([])) // reset selected interest options
    dispatch(setAppliedInterestTags([])) // reset applied interest tags
    dispatch(setCurrentSelectedFilter({})) // reset current selected filter

    // reset selected prospects list while adding prospects
    dispatch(setAddToSavedProspects([]))

    // reset all counts
    dispatch(count(0))
    dispatch(savedProspectsCount(0))
    dispatch(setAllConstituentsTotalCount(0))

    // reset saved prospects list
    dispatch(constituentIdList([]))
    dispatch(setListDescription('')) // reset saved prospects list description
    dispatch(allSavedProspectsList([])) // reset selected prospects while adding to saved prospects

    // reset search terms
    dispatch(setSearchTermQuery({previousTerm: '', currentTerm: ''})) // reset ask give prism chatbot search term
    dispatch(setSearchQuery('')) // reset ask give prism chatbot search query
    dispatch(updatedAllProspectsSearchTerm({currentTerm: '', previousTerm: ''})) // reset all prospects search term
    dispatch(updatedSavedProspectsSearchTerm({currentTerm: '', previousTerm: ''})) // reset all saved prospects search term
    dispatch(setResetSearchTerm(true)) // reset search term

    // reset action names
    dispatch(setCampaignAction({actionName: '', name: ''})) // reset campaign action

    // reset selected tab to all prospects
    dispatch(selectedTab('allProspects')) // reset selected tab to all prospects

    // reset button name for chatbot
    dispatch(setButtonViewName('Switch to manual view')) // reset button view name
    dispatch(setSwitchFilters(false)) // reset switch filters

    // reset campaign view
    dispatch(setCampaignView('defaultView')) // reset campaign view

    // chatbot related resets
    dispatch(setMessage('')) // reset chat message
    dispatch(setMatchedProspectId([])) // reset matched prospect ids
    dispatch(setInterestTagId([])) // reset interest tag ids
    dispatch(setMatchedProspectList([])) // reset matched prospect list
    dispatch(setChatbotData([])) // reset chatbot data
    dispatch(setShouldResetChatView(true)) // reset chatbot view

    dispatch(setSortColumn(null))
    dispatch(setSortDirection('asc'))
    dispatch(setSortConfig({direction: 'asc', key: null}))

    setOpenDialog(false) // close the dialog
  }

  return (
    <>
      <CustomSearchableDropdown
        sx={{width: '35%'}}
        options={transformedTenantList || []}
        label="Select Tenant"
        loading={loading}
        value={selectedTenant}
        disableClearable={true}
        onChange={(value) => {
          if (value) {
            handleValueChange(value)
          }
        }}
        placeholder="Select Tenant"
      />

      <CustomDialog
        open={openDialog}
        showCloseBtn={false}
        showDivider={true}
        maxWidth="xs"
        timelineDialog={true}
        title={
          <StyledTitleContainer sx={{fontSize: theme.typography.heading.fontSize.l}}>
            Confirm Tenant Switch
          </StyledTitleContainer>
        }
        content={<TenantConfirmationPopup selectedTenantName={selectedTenant?.name} />}
        actions={
          <StyledActionBox>
            <StyledActionResetButton onClick={handleCancel}>Cancel</StyledActionResetButton>
            <StyledActionAddButton onClick={handleConfirm}>Confirm</StyledActionAddButton>
          </StyledActionBox>
        }
      />
    </>
  )
}

TenantConfirmationPopup.propTypes = {
  selectedTenantName: PropTypes.string
}

export default SuperUserIndex
